import { useState, useEffect } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { saveNeedsFromNotes } from '../../utils/needsService';
import { useAuth } from '../../contexts/AuthContext';
import { AcademieReport } from '../../types/academieReport';
import { FormInput } from '../ui/FormInput';
import { WorshipReportSelect } from '../ui/WorshipReportSelect';
import { WorshipReport } from '../../types/worshipReport';
import { toast } from 'sonner';

interface AcademieFormProps {
  onSubmit?: (data: AcademieReport) => void;
  onCancel?: () => void;
}

export default function AcademieForm({ onSubmit, onCancel }: AcademieFormProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<AcademieReport>>({
    date: '',
    worshipReportId: '',
    className: '',
    actualStudents: 0,
    presentStudents: 0,
    moderator: '',
    courseOfTheDay: '',
    spiritualAtmosphere: '',
    classParticipation: '',
    nextCourse: '',
    nextModerator: '',
    nextMeditation: '',
    nextExercise: '',
    needsNotes: ''
  });

  // Auto-populate date from selected worship report
  useEffect(() => {
    const fetchWorshipReportData = async () => {
      if (!formData.worshipReportId) {
        setFormData(prev => ({ ...prev, date: '' }));
        return;
      }

      try {
        const worshipReportRef = doc(db, 'worshipReports', formData.worshipReportId);
        const worshipReportSnap = await getDoc(worshipReportRef);
        
        if (worshipReportSnap.exists()) {
          const worshipReportData = worshipReportSnap.data() as WorshipReport;
          setFormData(prev => ({
            ...prev,
            date: worshipReportData.date
          }));
        }
      } catch (error) {
        console.error('Error fetching worship report:', error);
      }
    };

    fetchWorshipReportData();
  }, [formData.worshipReportId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!formData.date || !formData.className || !formData.moderator || !formData.courseOfTheDay) {
        throw new Error('Missing required fields');
      }
      
      if (!formData.worshipReportId) {
        throw new Error('Veuillez sélectionner le rapport de culte à annexer');
      }

      const reportData = {
        ...formData,
        actualStudents: formData.actualStudents || 0,
        presentStudents: formData.presentStudents || 0,
        createdAt: new Date(),
        createdBy: user?.id
      } as AcademieReport;

      const docRef = await addDoc(collection(db, 'academieReports'), reportData);
      
      // Sauvegarder les besoins si présents
      if (formData.needsNotes) {
        await saveNeedsFromNotes({
          reportId: docRef.id,
          reportType: 'academie',
          department: 'Académie',
          needsNotes: formData.needsNotes,
          reportDate: formData.date!
        });
      }
      
      if (onSubmit) {
        onSubmit({ ...reportData, id: docRef.id });
      }

      // Reset form
      setFormData({
        date: '',
        worshipReportId: '',
        className: '',
        actualStudents: 0,
        presentStudents: 0,
        moderator: '',
        courseOfTheDay: '',
        spiritualAtmosphere: '',
        classParticipation: '',
        nextCourse: '',
        nextModerator: '',
        nextMeditation: '',
        nextExercise: '',
        needsNotes: ''
      });

      toast.success('Rapport Académie enregistré avec succès');
    } catch (error) {
      console.error('Error saving Academie report:', error);
      toast.error('Erreur lors de l\'enregistrement du rapport Académie');
    }
  };

  const handleChange = (field: keyof AcademieReport, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-6">Rapport Académie</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <WorshipReportSelect
              value={formData.worshipReportId || ''}
              onChange={(value) => setFormData(prev => ({ ...prev, worshipReportId: value }))}
              required
              departmentCollection="academieReports"
            />
          </div>
          <FormInput
            label="Date du rapport"
            type="date"
            value={formData.date || ''}
            onChange={(e) => handleChange('date', e.target.value)}
           disabled={true}
          />
          <FormInput
            label="Classe"
            placeholder="ex: METANOÏA"
            value={formData.className || ''}
            onChange={(e) => handleChange('className', e.target.value)}
            required
          />
          <FormInput
            label="Effectif réel"
            type="number"
            min="0"
            value={formData.actualStudents || 0}
            onChange={(e) => handleChange('actualStudents', parseInt(e.target.value) || 0)}
            required
          />
          <FormInput
            label="Effectif présent"
            type="number"
            min="0"
            value={formData.presentStudents || 0}
            onChange={(e) => handleChange('presentStudents', parseInt(e.target.value) || 0)}
            required
          />
          <FormInput
            label="Modérateur du jour"
            placeholder="ex: KPEYA FRED"
            value={formData.moderator || ''}
            onChange={(e) => handleChange('moderator', e.target.value)}
            required
          />
          <FormInput
            label="Cours du jour"
            placeholder="ex: NOUVELLE CRÉATION (fin)"
            value={formData.courseOfTheDay || ''}
            onChange={(e) => handleChange('courseOfTheDay', e.target.value)}
            required
          />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Atmosphère spirituelle de la classe
            </label>
            <textarea
              placeholder="Observations sur l'atmosphère..."
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
              value={formData.spiritualAtmosphere || ''}
              onChange={(e) => handleChange('spiritualAtmosphere', e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Participation de la classe
            </label>
            <textarea
              placeholder="Détails sur la participation..."
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
              value={formData.classParticipation || ''}
              onChange={(e) => handleChange('classParticipation', e.target.value)}
            />
          </div>
          <FormInput
            label="Prochain cours"
            placeholder="ex: Le péché et ses conséquences"
            value={formData.nextCourse || ''}
            onChange={(e) => handleChange('nextCourse', e.target.value)}
          />
          <FormInput
            label="Prochain modérateur"
            placeholder="ex: TUEHI REGINA"
            value={formData.nextModerator || ''}
            onChange={(e) => handleChange('nextModerator', e.target.value)}
          />
          <FormInput
            label="Méditation prochaine"
            placeholder="ex: MARC 1 à 4"
            value={formData.nextMeditation || ''}
            onChange={(e) => handleChange('nextMeditation', e.target.value)}
          />
          <FormInput
            label="Exercice prochain"
            placeholder="ex: RAS"
            value={formData.nextExercise || ''}
            onChange={(e) => handleChange('nextExercise', e.target.value)}
          />
        </div>
      </div>

      {/* Besoins */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Liste des besoins</h2>
        <textarea
          placeholder="Listez les besoins du département (un besoin par ligne)&#10;Exemple:&#10;- Projecteur pour les présentations&#10;- Tableau blanc pour les exercices&#10;- Chaises supplémentaires"
          className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
          value={formData.needsNotes || ''}
          onChange={(e) => handleChange('needsNotes', e.target.value)}
        />
        <p className="mt-1 text-xs text-gray-500">
          Entrez un besoin par ligne. Ces besoins seront ajoutés à la liste de suivi des besoins du département.
        </p>
      </div>

      <div className="flex justify-end space-x-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Annuler
          </button>
        )}
        <button
          type="submit"
          className="px-6 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2"
        >
          Enregistrer le rapport Académie
        </button>
      </div>
    </form>
  );
}